import { useState, useEffect, FormEvent } from 'react';
import apiClient from '../../services/api';
import { Skill } from '../../types/auth';
import * as skillManagementService from '../../services/skillManagementService';
import { showToast } from '../ui/Toast';
import { ConfirmDialog } from '../ui/ConfirmDialog';

type ViewMode = 'grid' | 'list';
type SortOption = 'name-asc' | 'name-desc' | 'created-asc' | 'created-desc';

const SkillManagement = () => {
    const [skills, setSkills] = useState<Skill[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingSkill, setEditingSkill] = useState<Skill | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({ name: '', description: '' });
    const [error, setError] = useState('');
    const [seedLoading, setSeedLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortOption, setSortOption] = useState<SortOption>('name-asc');
    const [viewMode, setViewMode] = useState<ViewMode>('grid');
    const [pendingAction, setPendingAction] = useState<{ type: 'delete' | 'seed'; id?: string } | null>(null);

    useEffect(() => {
        fetchSkills();
    }, []);

    const fetchSkills = async () => {
        try {
            const response = await apiClient.get('/skills');
            setSkills(response.data.skills || response.data.data || []);
        } catch (error) {
            console.error('Failed to fetch skills:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError('');

        try {
            if (editingSkill) {
                await apiClient.put(`/skills/${editingSkill.id}`, formData);
            } else {
                await apiClient.post('/skills', formData);
            }

            setIsModalOpen(false);
            setEditingSkill(null);
            setFormData({ name: '', description: '' });
            fetchSkills();
        } catch (err: any) {
            setError(err.response?.data?.error?.message || 'Failed to save skill');
        }
    };

    const handleDelete = async (id: string) => {
        try {
            await apiClient.delete(`/skills/${id}`);
            setPendingAction(null);
            showToast('Skill deleted successfully', 'success');
            fetchSkills();
        } catch (error) {
            console.error('Failed to delete skill:', error);
            showToast('Failed to delete skill', 'error');
        }
    };

    const handleSeedInitialSkills = async () => {
        setPendingAction(null);
        setSeedLoading(true);
        setError('');
        try {
            await skillManagementService.seedInitialSkills();
            showToast('Initial skills seeded and assigned successfully!', 'success');
            fetchSkills();
        } catch (err: any) {
            setError(err.response?.data?.error?.message || 'Failed to seed initial skills');
        } finally {
            setSeedLoading(false);
        }
    };

    const openModal = (skill?: Skill) => {
        if (skill) {
            setEditingSkill(skill);
            setFormData({ name: skill.name, description: skill.description || '' });
        } else {
            setEditingSkill(null);
            setFormData({ name: '', description: '' });
        }
        setIsModalOpen(true);
    };

    // Filter and sort skills
    const filteredAndSortedSkills = skills
        .filter((skill) => {
            if (!searchQuery) return true;
            const query = searchQuery.toLowerCase();
            return (
                skill.name.toLowerCase().includes(query) ||
                (skill.description && skill.description.toLowerCase().includes(query))
            );
        })
        .sort((a, b) => {
            switch (sortOption) {
                case 'name-asc':
                    return a.name.localeCompare(b.name);
                case 'name-desc':
                    return b.name.localeCompare(a.name);
                case 'created-asc':
                    const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                    const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                    return aDate - bDate;
                case 'created-desc':
                    const aDateDesc = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                    const bDateDesc = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                    return bDateDesc - aDateDesc;
                default:
                    return 0;
            }
        });

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <>
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Skill Management</h1>
                    <p className="text-slate-500 mt-1">Manage skills and certifications</p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setPendingAction({ type: 'seed' })}
                        className="btn-secondary flex items-center gap-2"
                        disabled={seedLoading}
                    >
                        {seedLoading ? 'Seeding...' : 'Seed Initial Skills'}
                    </button>
                    <button
                        onClick={() => openModal()}
                        className="btn-primary flex items-center gap-2"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Skill
                    </button>
                </div>
            </div>

            {error && (
                <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm border border-red-100">
                    {error}
                </div>
            )}

            {/* Search, Sort, and View Controls */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200">
                <div className="flex flex-col md:flex-row gap-4 items-center">
                    {/* Search */}
                    <div className="flex-1 w-full md:w-auto">
                        <div className="relative">
                            <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                                type="text"
                                placeholder="Search skills..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    {/* Sort */}
                    <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-slate-700">Sort:</label>
                        <select
                            value={sortOption}
                            onChange={(e) => setSortOption(e.target.value as SortOption)}
                            className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="name-asc">Name (A-Z)</option>
                            <option value="name-desc">Name (Z-A)</option>
                            <option value="created-asc">Created (Oldest)</option>
                            <option value="created-desc">Created (Newest)</option>
                        </select>
                    </div>

                    {/* View Toggle */}
                    <div className="flex items-center gap-2 border border-slate-300 rounded-lg p-1">
                        <button
                            onClick={() => setViewMode('grid')}
                            className={`p-2 rounded ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                            title="Grid View"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                            </svg>
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            className={`p-2 rounded ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                            title="List View"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Results count */}
                <div className="mt-3 text-sm text-slate-600">
                    Showing {filteredAndSortedSkills.length} of {skills.length} skills
                </div>
            </div>

            {/* Skills Display */}
            {filteredAndSortedSkills.length === 0 ? (
                <div className="bg-white p-12 rounded-lg shadow-sm border border-slate-200 text-center">
                    <svg className="w-16 h-16 text-slate-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-slate-600 text-lg font-medium">No skills found</p>
                    <p className="text-slate-500 mt-1">
                        {searchQuery ? 'Try adjusting your search query' : 'Click "Add Skill" to create your first skill'}
                    </p>
                </div>
            ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredAndSortedSkills.map((skill) => (
                    <div key={skill.id} className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-4">
                            <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => openModal(skill)}
                                    className="text-slate-400 hover:text-blue-600 transition-colors"
                                >
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                </button>
                                <button
                                    onClick={() => setPendingAction({ type: 'delete', id: skill.id })}
                                    className="text-slate-400 hover:text-red-600 transition-colors"
                                >
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <h3 className="text-lg font-semibold text-slate-800 mb-2">{skill.name}</h3>
                        <p className="text-slate-500 text-sm">{skill.description || 'No description provided'}</p>
                    </div>
                ))}
            </div>
            ) : (
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Skill Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Description</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-200">
                            {filteredAndSortedSkills.map((skill) => (
                                <tr key={skill.id} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-3">
                                            <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
                                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <span className="text-sm font-semibold text-slate-800">{skill.name}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <p className="text-sm text-slate-600">{skill.description || 'No description provided'}</p>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => openModal(skill)}
                                                className="text-blue-600 hover:text-blue-800 transition-colors"
                                                title="Edit"
                                            >
                                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => setPendingAction({ type: 'delete', id: skill.id })}
                                                className="text-red-600 hover:text-red-800 transition-colors"
                                                title="Delete"
                                            >
                                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl animate-fade-in">
                        <h2 className="text-2xl font-bold text-slate-800 mb-6">
                            {editingSkill ? 'Edit Skill' : 'Create Skill'}
                        </h2>

                        {error && (
                            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm border border-red-100">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Skill Name</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                    className="input-field"
                                    placeholder="e.g. Forklift Certified"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="input-field min-h-[100px]"
                                    placeholder="Optional description..."
                                />
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="flex-1 btn-primary">
                                    {editingSkill ? 'Save Changes' : 'Create Skill'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>

            <ConfirmDialog
                open={!!pendingAction}
                title={pendingAction?.type === 'delete' ? 'Delete Skill' : 'Seed Initial Skills'}
                message={pendingAction?.type === 'delete'
                    ? 'Are you sure? This will unassign it from all users.'
                    : 'This will seed initial skills and assign them to users. Proceed?'}
                confirmLabel={pendingAction?.type === 'delete' ? 'Delete' : 'Seed Skills'}
                variant={pendingAction?.type === 'delete' ? 'danger' : 'warning'}
                onConfirm={() => {
                    if (pendingAction?.type === 'delete' && pendingAction.id) handleDelete(pendingAction.id);
                    else if (pendingAction?.type === 'seed') handleSeedInitialSkills();
                }}
                onCancel={() => setPendingAction(null)}
            />
        </>
    );
};

export default SkillManagement;
